import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { notify } from "../util/notify";
import { addToGuestCart, openCartDrawer } from "../util/guestCartService";
import { addRecentlyViewed, getRecentlyViewed } from "../util/recentViewedService";
import { isInWishlist, toggleWishlist } from "../util/wishlistService";
import { applyStoreTitle, extractProductImagePath } from "../util/storeLogoHelper";
import { peekStoreSettings } from "../util/storeSettings";
import { trackConfiguredMetaPixel } from "../util/metaPixel";

// The variant that matches every chosen option, or null.
export function matchVariant(product, selected) {
  const variants = product?.variant_catalog?.variants || [];
  const options = product?.variant_catalog?.options || [];
  if (!variants.length) return null;
  return variants.find((variant) =>
    options.every((option) => String(variant.options?.[option.name] || "") === String(selected?.[option.name] || ""))
  ) || null;
}

/**
 * Everything a product page needs, for any store design: the product, its
 * price and stock for the chosen options, quantity, cart, wishlist, reviews,
 * and recently viewed. A theme's product template only lays these out.
 * Prices come from the server (`pricing`, `variant_catalog`).
 */
export default function useProduct(categorySlug, productSlug) {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  // Shown on the page, not in popups: why the product cannot be added yet,
  // and how the last review submission went ({ type, text }).
  const [buyError, setBuyError] = useState("");
  const [reviewMessage, setReviewMessage] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [inWishlist, setInWishlist] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 5.0 });

  const loadReviews = useCallback((productId) => {
    if (!productId) return;
    axios.get(`/api/product-reviews/${productId}`).then((res) => {
      if (res.data.status === 200) {
        setReviews(res.data.reviews || []);
        setReviewStats({ total: res.data.total || 0, average: res.data.average || 5.0 });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`/api/view-productDetails/${categorySlug}/${productSlug}`).then((res) => {
      if (!mounted) return;
      if (res.data.status === 200) {
        const prod = res.data.product || res.data.productDetails;
        if (prod) {
          setProduct(prod);
          applyStoreTitle(null, prod.name);
          setInWishlist(isInWishlist(prod.id));
          loadReviews(prod.id);
          const validCategorySlug = categorySlug && categorySlug !== "undefined" ? categorySlug : prod.category?.slug;
          if (categorySlug === "undefined" && prod.category?.slug) {
            history.replace(`/collection/${prod.category.slug}/${prod.slug}`);
          }
          addRecentlyViewed({
            id: prod.id,
            name: prod.name,
            slug: prod.slug,
            categorySlug: validCategorySlug,
            categoryName: prod.category?.name,
            category: prod.category,
            product_image: prod.product_image,
            productprices: prod.productprices,
            sellingPrice: prod.pricing?.final_price,
          });
          setRecentlyViewed(getRecentlyViewed().filter((item) => String(item.id) !== String(prod.id)));
        }
      } else if (res.data.status === 400 || res.data.status === 404) {
        notify(res.data.message || "This product is not available.", "error");
        history.push("/collection");
      }
      setLoading(false);
    }).catch(() => {
      if (mounted) setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [categorySlug, productSlug, history, loadReviews]);

  // Start on the first option that is in stock when a different product opens.
  useEffect(() => {
    const variants = product?.variant_catalog?.variants || [];
    const options = product?.variant_catalog?.options || [];
    const first = variants.find((variant) => Number(variant.qty) > 0) || variants[0];
    if (first?.options) {
      setSelectedOptions({ ...first.options });
    } else {
      const initial = {};
      options.forEach((option) => {
        initial[option.name] = option.values?.[0] || "";
      });
      setSelectedOptions(initial);
    }
    setQuantity(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const pricing = product?.pricing || {};
  const activeVariant = matchVariant(product, selectedOptions);
  const hasVariantChoices = (product?.variant_catalog?.variants || []).length > 0;
  const currentPrice = activeVariant
    ? activeVariant.final_price
    : (pricing.final_price ?? product?.productprices?.seeling_price ?? 0);
  const sellingPrice = activeVariant
    ? activeVariant.price
    : (pricing.selling_price ?? product?.productprices?.seeling_price ?? 0);
  const hasDiscount = activeVariant
    ? Number(activeVariant.final_price) < Number(activeVariant.price)
    : (pricing.has_discount ?? false);
  const discountLabel = pricing.discount_label || (pricing.discount_percent ? `${pricing.discount_percent}% OFF` : "");
  const stockQty = activeVariant
    ? Number(activeVariant.qty || 0)
    : (pricing.stock_qty ?? product?.productprices?.qty ?? 0);
  const inStock = hasVariantChoices ? Boolean(activeVariant) && stockQty > 0 : (pricing.in_stock ?? stockQty > 0);
  const maxQty = Math.min(10, activeVariant ? Number(activeVariant.qty) : (pricing.stock_qty || product?.productprices?.qty || 10));

  useEffect(() => {
    if (!product?.id) return;
    trackConfiguredMetaPixel("ViewContent", {
      content_ids: [String(product.id)],
      content_name: product.name,
      content_type: "product",
      value: Number(currentPrice || 0),
      currency: peekStoreSettings()?.currency || "PKR",
    }).catch(() => {});
    // Fire once when a new product is opened, not when its option changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const decrementQty = () => setQuantity((prev) => (prev > 1 ? prev - 1 : prev));
  const incrementQty = () => setQuantity((prev) => (prev < maxQty ? prev + 1 : prev));

  // Checks the chosen options can be bought; shows why not.
  const readyToBuy = () => {
    if (!product?.id) return false;
    if (hasVariantChoices && !activeVariant) {
      setBuyError("Choose every option (like size or color) before adding this product.");
      return false;
    }
    if (activeVariant && Number(activeVariant.qty) < 1) {
      setBuyError(`${activeVariant.title} is out of stock. Choose another option.`);
      return false;
    }
    setBuyError("");
    return true;
  };

  const addToCart = () => {
    if (!readyToBuy()) return;
    addToGuestCart(product, quantity, activeVariant);
    trackConfiguredMetaPixel("AddToCart", {
      content_ids: [String(product.id)],
      content_name: product.name,
      content_type: "product",
      value: Number(currentPrice || 0) * Number(quantity || 1),
      currency: peekStoreSettings()?.currency || "PKR",
      contents: [{ id: String(product.id), quantity: Number(quantity || 1) }],
    }).catch(() => {});
    // The cart drawer opening is the confirmation.
    openCartDrawer();
  };

  const buyNow = () => {
    if (!readyToBuy()) return;
    addToGuestCart(product, quantity, activeVariant);
    trackConfiguredMetaPixel("AddToCart", {
      content_ids: [String(product.id)],
      content_name: product.name,
      content_type: "product",
      value: Number(currentPrice || 0) * Number(quantity || 1),
      currency: peekStoreSettings()?.currency || "PKR",
      contents: [{ id: String(product.id), quantity: Number(quantity || 1) }],
    }).catch(() => {});
    history.push("/checkout");
  };

  const toggleProductWishlist = (image) => {
    if (!product?.id) return;
    const isNowIn = toggleWishlist({
      id: product.id,
      name: product.name,
      slug: product.slug,
      categorySlug,
      categoryName: product.category?.name,
      image: image || extractProductImagePath(product) || product?.image,
      price: product?.pricing?.final_price || product.productprices?.seeling_price,
    });
    setInWishlist(isNowIn);
    notify(isNowIn ? "Saved to your wishlist" : "Removed from your wishlist", isNowIn ? "success" : "info");
  };

  // Sends a review; resolves true when it was accepted.
  const submitReview = ({ name, rating, comment }) => {
    if (!product?.id) return Promise.resolve(false);
    setReviewMessage(null);
    return axios.post("/api/product-reviews", { product_id: product.id, name, rating: Number(rating), comment })
      .then((res) => {
        if (res.data.status === 200) {
          loadReviews(product.id);
          setReviewMessage({ type: "success", text: res.data.message || "Thank you! Your review was submitted." });
          return true;
        }
        setReviewMessage({ type: "error", text: res.data.message || "Could not submit the review." });
        return false;
      })
      .catch(() => {
        setReviewMessage({ type: "error", text: "Could not submit the review. Please try again." });
        return false;
      });
  };

  // A new choice clears the "choose an option" message.
  useEffect(() => {
    setBuyError("");
  }, [activeVariant, product?.id]);

  return {
    loading,
    product,
    pricing,
    selectedOptions,
    setSelectedOptions,
    activeVariant,
    hasVariantChoices,
    currentPrice,
    sellingPrice,
    hasDiscount,
    discountLabel,
    stockQty,
    inStock,
    quantity,
    setQuantity,
    decrementQty,
    incrementQty,
    addToCart,
    buyNow,
    inWishlist,
    toggleWishlist: toggleProductWishlist,
    recentlyViewed,
    reviews,
    reviewStats,
    submitReview,
    reviewMessage,
    setReviewMessage,
    buyError,
  };
}
